import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Send, Clock } from 'lucide-react';
import { listEgressTargets, getEgressTarget, publishArticle } from '../lib/api/index.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/modal.js';

interface PublishTarget {
  target_name: string;
  destination_name?: string;
}

interface PublishDialogProps {
  isOpen: boolean;
  onClose: () => void;
  articleId: string;
  onSuccess: (targetName: string) => void;
  publishedTargets?: PublishTarget[];
  /** Article's home target — pre-selected when the dialog opens.
   *  Operators can still pick another target (siblings/cross-posts)
   *  but the default is the site the article was generated for. */
  defaultTargetName?: string | null;
}

export function PublishDialog({ isOpen, onClose, articleId, onSuccess, publishedTargets, defaultTargetName }: PublishDialogProps) {
  const queryClient = useQueryClient();
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [selectedDestination, setSelectedDestination] = useState<string>('');
  const [scheduledPublish, setScheduledPublish] = useState(false);
  const [publishAt, setPublishAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: targetsData } = useQuery({
    queryKey: ['egress-targets'],
    queryFn: listEgressTargets,
    enabled: isOpen,
  });

  const activeTargets = targetsData?.items.filter(t => t.is_active) || [];
  const selectedTarget = activeTargets.find(t => t.target_id === selectedTargetId);

  // When the dialog opens (or targets load), pre-select the article's home
  // target if the operator hasn't already chosen one. Skip if the home target
  // is already fully published — the radio is disabled in that case.
  useEffect(() => {
    if (!isOpen || !defaultTargetName || selectedTargetId) return;
    const home = activeTargets.find(t => t.target_name === defaultTargetName);
    if (!home) return;
    const fullyPublished = isTargetFullyPublished(home.target_name);
    if (fullyPublished) return;
    setSelectedTargetId(home.target_id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultTargetName, activeTargets.length]);

  const { data: targetDetail } = useQuery({
    queryKey: ['egress-target', selectedTargetId],
    queryFn: () => getEgressTarget(selectedTargetId),
    enabled: !!selectedTargetId,
  });

  // Exclude main_branch destinations — production is managed via the Promote workflow (PR test→main)
  const destinations = targetDetail?.destinations.filter(d => d.is_active && d.destination_type !== 'main_branch') || [];

  const isDestinationPublished = (targetName: string, destinationName: string) =>
    publishedTargets?.some(pt => pt.target_name === targetName && (pt.destination_name === destinationName || (!pt.destination_name && !destinationName)));

  const isTargetFullyPublished = (targetName: string) => {
    if (!publishedTargets?.length) return false;
    const targetDests = targetDetail?.destinations.filter(d => d.is_active) || [];
    if (targetDests.length === 0) return publishedTargets.some(pt => pt.target_name === targetName);
    return targetDests.every(d => isDestinationPublished(targetName, d.destination_name));
  };

  const handleTargetChange = (targetId: string) => {
    setSelectedTargetId(targetId);
    setSelectedDestination('');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedTarget) return;

    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await publishArticle(articleId, {
        target_name: selectedTarget.target_name,
        destination_name: selectedDestination || undefined,
        publish_at: scheduledPublish && publishAt ? new Date(publishAt).toISOString() : undefined,
      });

      setSuccessMessage(
        scheduledPublish && publishAt
          ? `Publish scheduled for ${new Date(publishAt).toLocaleString()}`
          : 'Publish job created. The article will be published shortly.'
      );

      // Notify parent with target name for immediate status feedback
      onSuccess(selectedTarget.target_name);

      setTimeout(() => {
        onClose();
        setSuccessMessage(null);
        setSelectedTargetId('');
        setSelectedDestination('');
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create publish job');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose} canClose={!isSubmitting} size="lg">
      <ModalHeader>
        <div className="flex items-center space-x-3">
          <Send className="h-6 w-6 text-primary-600" />
          <h2 className="text-xl sm:text-2xl font-bold text-grey-900">Publish Article</h2>
        </div>
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="text-grey-400 hover:text-grey-600 transition-colors disabled:opacity-50 touch-target"
        >
          <X className="h-6 w-6" />
        </button>
      </ModalHeader>

      <ModalBody>
          <p className="text-grey-600 mb-6 text-base">
            Choose a target and destination to publish this article.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-danger-50 border-2 border-danger-200 text-danger-700 rounded-lg">
              <p className="font-semibold">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-success-50 border-2 border-success-200 text-success-700 rounded-lg">
              <p className="font-semibold">{successMessage}</p>
            </div>
          )}

          {/* Target Selection */}
          <div className="mb-6">
            <label className="block text-base font-bold text-grey-900 mb-3">
              Target
            </label>
            <div className="space-y-2">
              {activeTargets.map((target) => {
                const fullyPublished = isTargetFullyPublished(target.target_name);
                return (
                  <label
                    key={target.target_id}
                    className={`flex items-start p-4 border-2 rounded-lg transition-all ${
                      fullyPublished
                        ? 'border-grey-200 bg-grey-50 cursor-not-allowed opacity-60'
                        : selectedTargetId === target.target_id
                        ? 'border-primary-500 bg-primary-50 shadow-sm cursor-pointer'
                        : 'border-grey-200 hover:border-primary-300 hover:bg-grey-50 cursor-pointer'
                    }`}
                  >
                    <input
                      type="radio"
                      name="target"
                      value={target.target_id}
                      checked={selectedTargetId === target.target_id}
                      onChange={(e) => handleTargetChange(e.target.value)}
                      disabled={fullyPublished}
                      className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-grey-900">{target.target_name}</span>
                        {fullyPublished && (
                          <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-success-100 text-success-700">
                            Published
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-grey-600 mt-0.5">{target.target_type}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Destination Selection */}
          {destinations.length > 0 && (
            <div className="mb-6">
              <label className="block text-base font-bold text-grey-900 mb-3">
                Destination
              </label>
              <div className="space-y-2">
                {destinations.map((dest) => {
                const destPublished = isDestinationPublished(selectedTarget?.target_name || '', dest.destination_name);
                return (
                  <label
                    key={dest.destination_id}
                    className={`flex items-start p-4 border-2 rounded-lg transition-all ${
                      destPublished
                        ? 'border-grey-200 bg-grey-50 cursor-not-allowed opacity-60'
                        : selectedDestination === dest.destination_name
                        ? 'border-primary-500 bg-primary-50 shadow-sm cursor-pointer'
                        : 'border-grey-200 hover:border-primary-300 hover:bg-grey-50 cursor-pointer'
                    }`}
                  >
                    <input
                      type="radio"
                      name="destination"
                      value={dest.destination_name}
                      checked={selectedDestination === dest.destination_name}
                      onChange={(e) => setSelectedDestination(e.target.value)}
                      disabled={destPublished}
                      className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-grey-900">{dest.destination_name}</span>
                        {destPublished && (
                          <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-success-100 text-success-700">
                            Published
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-grey-600 mt-0.5">
                        {dest.destination_type}
                        {dest.config.branch && ` (${dest.config.branch})`}
                      </div>
                    </div>
                  </label>
                );
              })}
              </div>
            </div>
          )}

          {/* Schedule Publish */}
          {selectedTargetId && (
            <div className="mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={scheduledPublish}
                  onChange={e => {
                    setScheduledPublish(e.target.checked);
                    if (!e.target.checked) setPublishAt('');
                  }}
                  className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <Clock className="h-4 w-4 text-grey-500" />
                <span className="text-sm font-medium text-grey-700">Schedule for later</span>
              </label>
              {scheduledPublish && (
                <input
                  type="datetime-local"
                  value={publishAt}
                  onChange={e => setPublishAt(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="mt-2 w-full border-2 border-grey-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              )}
            </div>
          )}

      </ModalBody>

      <ModalFooter>
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="px-6 py-2.5 border-2 border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 hover:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedTargetId}
          className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              <span>Publishing...</span>
            </>
          ) : (
            <>
              {scheduledPublish ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              <span>{scheduledPublish && publishAt ? 'Schedule' : 'Publish'}</span>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}
